// PUSH-NUDGE-001 — localStorage bookkeeping for the post-login push notification nudge
// banner (src/components/PushNudgeBanner.js). Kept out of the component so it's unit
// testable without rendering, and so every localStorage touch goes through the same
// try/catch (private/incognito browsing throws on both get and set).
export const PUSH_NUDGE_STORAGE_KEY = "webtime.pushNudge";

// AC (per feature spec): dismiss "Not now" for 14 days, then re-offer the nudge.
export const DISMISS_DAYS = 14;
const DISMISS_MS = DISMISS_DAYS * 24 * 60 * 60 * 1000;

const readState = () => {
  try {
    const raw = window.localStorage.getItem(PUSH_NUDGE_STORAGE_KEY);
    if (!raw) {
      return null;
    }
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === "object" ? parsed : null;
  } catch (err) {
    // Private mode (Safari) can throw on read too, not just write — treat exactly like
    // "nothing stored" so the banner still renders rather than crashing the page.
    return null;
  }
};

// Merges into whatever is already stored (via readState()) rather than replacing it
// outright — otherwise a later dismissPushNudge()/markPushNudgeEnabled() call clobbers a
// field the other one wrote (e.g. `enabled: true` clobbering the still-useful
// `dismissedAt`, or vice versa).
const writeState = (patch) => {
  try {
    const merged = { ...readState(), ...patch };
    window.localStorage.setItem(PUSH_NUDGE_STORAGE_KEY, JSON.stringify(merged));
  } catch (err) {
    // Best-effort only (e.g. private-mode Safari, quota exceeded) — the nudge simply
    // re-offers next load instead of throwing out of a click handler.
  }
};

// Marks "Not now" — re-offer the banner after DISMISS_DAYS.
export const dismissPushNudge = (now = new Date()) => {
  writeState({ dismissedAt: now.toISOString() });
};

// Marks push as successfully turned on — hide the banner forever in this browser.
export const markPushNudgeEnabled = () => {
  writeState({ enabled: true });
};

// True when the banner should stay hidden: either push was enabled from it before, or it
// was dismissed less than DISMISS_DAYS ago.
export const isPushNudgeSuppressed = (now = new Date()) => {
  const state = readState();
  if (!state) {
    return false;
  }
  if (state.enabled === true) {
    return true;
  }
  if (!state.dismissedAt) {
    return false;
  }
  const dismissedAt = new Date(state.dismissedAt);
  if (Number.isNaN(dismissedAt.getTime())) {
    return false;
  }
  return now.getTime() - dismissedAt.getTime() < DISMISS_MS;
};

// PUSH-NUDGE-002 (banner → modal). sessionStorage (not localStorage) session guard, kept as
// a belt-and-suspenders backstop alongside dismissPushNudge()'s localStorage write above:
// writeState() is best-effort (private-mode Safari etc. can silently fail to persist), which
// would otherwise let the modal reopen on every route change/remount within the same tab
// after the user already said "Not now"/"Got it". A separate sessionStorage key survives
// that failure mode independently, and is naturally scoped to "this login session" (cleared
// when the tab closes) — exactly the "at most once per session" guarantee asked for here,
// regardless of how many times the component remounts.
export const PUSH_NUDGE_SESSION_STORAGE_KEY = "webtime.pushNudge.shownThisSession";

export const markPushNudgeShownThisSession = () => {
  try {
    window.sessionStorage.setItem(PUSH_NUDGE_SESSION_STORAGE_KEY, "1");
  } catch (err) {
    // Best-effort only, same reasoning as writeState() above.
  }
};

export const wasPushNudgeShownThisSession = () => {
  try {
    return window.sessionStorage.getItem(PUSH_NUDGE_SESSION_STORAGE_KEY) === "1";
  } catch (err) {
    return false;
  }
};
